import axios from 'axios';
import {
  Supplier,
  SupplierProduct,
  PurchaseOrder,
  SupplierMetrics,
  SupplierFilter,
  SupplierPerformance,
  SupplierAlert
} from '../types';
import { authService } from '../../../services/authService';
import config from '../../../config';

const API_BASE_URL = `${config.api.baseUrl}/api`;

// Get auth headers from auth service
const getAuthHeaders = () => authService.getAuthHeaders();

class SupplierService {
  // Get suppliers with filters and last order date
  async getSuppliers(filter?: SupplierFilter): Promise<{
    suppliers: Supplier[];
    total: number;
    page: number;
    pageSize: number;
  }> {
    const response = await axios.post(
      `${API_BASE_URL}/suppliers/list`,
      { 
        filter,
        includeLastOrder: true,
        excludeArchived: true
      },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get single supplier by ID
  async getSupplier(supplierId: string): Promise<Supplier> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/${supplierId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Create new supplier
  async createSupplier(supplier: Omit<Supplier, 'SupplierID' | 'DateAdded'>): Promise<Supplier> {
    const response = await axios.post(
      `${API_BASE_URL}/suppliers/create`,
      supplier,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Update supplier
  async updateSupplier(supplierId: string, updates: Partial<Supplier>): Promise<Supplier> {
    const response = await axios.put(
      `${API_BASE_URL}/suppliers/${supplierId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get supplier products
  async getSupplierProducts(supplierId: string): Promise<SupplierProduct[]> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/${supplierId}/products`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get supplier metrics dashboard
  async getSupplierMetrics(): Promise<SupplierMetrics> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/metrics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get supplier performance
  async getSupplierPerformance(
    supplierId: string,
    period: 'monthly' | 'quarterly' | 'yearly'
  ): Promise<SupplierPerformance> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/${supplierId}/performance`,
      { 
        headers: getAuthHeaders(),
        params: { period }
      }
    );
    return response.data;
  }

  // Get supplier alerts
  async getSupplierAlerts(unresolved?: boolean): Promise<SupplierAlert[]> {
    const params = unresolved ? { unresolved: true } : {};
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/alerts`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  }

  // Purchase Order Management
  
  // Get purchase orders
  async getPurchaseOrders(params?: {
    supplierId?: string;
    status?: string[];
    startDate?: string;
    endDate?: string;
  }): Promise<PurchaseOrder[]> {
    const response = await axios.get(
      `${API_BASE_URL}/purchase-orders`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  }

  // Get single purchase order
  async getPurchaseOrder(orderId: string): Promise<PurchaseOrder> {
    const response = await axios.get(
      `${API_BASE_URL}/purchase-orders/${orderId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Create purchase order
  async createPurchaseOrder(order: {
    supplierId: string;
    items: Array<{
      sku: string;
      quantity: number;
      unitCost: number;
    }>;
    expectedDeliveryDate?: string;
    notes?: string;
  }): Promise<PurchaseOrder> {
    const response = await axios.post(
      `${API_BASE_URL}/purchase-orders/create`,
      order,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Update purchase order
  async updatePurchaseOrder(
    orderId: string,
    updates: Partial<PurchaseOrder>
  ): Promise<PurchaseOrder> {
    const response = await axios.put(
      `${API_BASE_URL}/purchase-orders/${orderId}`,
      updates,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Receive purchase order items
  async receivePurchaseOrder(orderId: string, items: Array<{
    itemId: string;
    receivedQuantity: number;
    notes?: string;
  }>): Promise<PurchaseOrder> {
    const response = await axios.post(
      `${API_BASE_URL}/purchase-orders/${orderId}/receive`,
      { items },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Cancel purchase order
  async cancelPurchaseOrder(orderId: string, reason: string): Promise<PurchaseOrder> {
    const response = await axios.post(
      `${API_BASE_URL}/purchase-orders/${orderId}/cancel`,
      { reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Export suppliers data
  async exportSuppliers(format: 'csv' | 'excel'): Promise<Blob> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/export`,
      { 
        headers: getAuthHeaders(),
        params: { format },
        responseType: 'blob'
      }
    );
    return response.data;
  }

  // Rate supplier
  async rateSupplier(supplierId: string, rating: {
    overall: number;
    delivery: number;
    quality: number;
    communication: number;
    notes?: string;
  }): Promise<Supplier> {
    const response = await axios.post(
      `${API_BASE_URL}/suppliers/${supplierId}/rate`,
      rating,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get suggested suppliers for a product
  async getSuggestedSuppliers(sku: string): Promise<Array<{
    supplier: Supplier;
    price: number;
    leadTime: number;
    reliability: number;
  }>> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/suggestions/${sku}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Analyze supplier pricing trends
  async getSupplierPricingTrends(supplierId: string, months: number = 12): Promise<{
    trends: Array<{
      month: string;
      averagePrice: number;
      priceChange: number;
      itemCount: number;
    }>;
    predictions: Array<{
      month: string;
      predictedPrice: number;
      confidence: number;
    }>;
  }> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/${supplierId}/pricing-trends`,
      { 
        headers: getAuthHeaders(),
        params: { months }
      }
    );
    return response.data;
  }

  // Block/unblock supplier
  async updateSupplierStatus(
    supplierId: string, 
    status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED',
    reason?: string
  ): Promise<Supplier> {
    const response = await axios.post(
      `${API_BASE_URL}/suppliers/${supplierId}/status`,
      { status, reason },
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Get supplier communication history
  async getSupplierCommunications(supplierId: string): Promise<Array<{
    date: Date;
    type: 'EMAIL' | 'PHONE' | 'MEETING' | 'OTHER';
    subject: string;
    notes: string;
    attachments?: string[];
  }>> {
    const response = await axios.get(
      `${API_BASE_URL}/suppliers/${supplierId}/communications`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }

  // Add communication log
  async addCommunicationLog(supplierId: string, communication: {
    type: 'EMAIL' | 'PHONE' | 'MEETING' | 'OTHER';
    subject: string;
    notes: string;
    attachments?: string[];
  }): Promise<void> {
    await axios.post(
      `${API_BASE_URL}/suppliers/${supplierId}/communications`,
      communication,
      { headers: getAuthHeaders() }
    );
  }
}

const supplierService = new SupplierService();
export default supplierService;
